/**
 * This component is used to let the merchant choose between two options for overriding
 * the cheapest rate found by a Rate Shopper, either a fixed amount or a percentage.
 *
 * We disable the input fields when the corresponding radio option is not selected, but preserve
 * the values in state so that when the user switches back to that option, their previous
 * input is still there. We do expect that the parent component will filter out and only send the
 * selected option's value to the backend.
 */

import React, { useCallback, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { CurrencyInput } from 'ui-components/components/currency-input';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { PercentageInput } from 'ui-components/components/percentage-input';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { Text, TextType } from 'ui-components/components/text';

import messages from './messages';
import style from './style.module.less';
import Global from '../../../../../store/global-selectors';

// @ts-expect-error less import

const emptyOverride = {
  costReduction: 0.0,
  costPercentReduction: 0.0,
  sellerProviderId: null,
  shippingServiceId: null,
};

const CURRENCY_OPTION = 'currency';
const PERCENT_OPTION = 'percent';

/**
 * @typedef {import('../../../../../prop-types/rate-shopper-cheapest-override').RateShopperCheapestOverride} RateShopperCheapestOverride
 * @typedef {import('../../../../../prop-types/rate-shopper-carrier-service').RateShopperCarrierService} RateShopperCarrierService
 */

/**
 * @param {object} props
 * @param {boolean} [props.disabled]
 * @param {RateShopperCheapestOverride|null} [props.cheapestOverride]
 * @param {RateShopperCarrierService[]} props.allowedCarrierServices
 * @param {(override: RateShopperCheapestOverride) => void} props.onShopperOverrideChange
 * @returns {React.ReactElement}
 */
export const CheapestOverrideOptions = ({ disabled, cheapestOverride = null, allowedCarrierServices, onShopperOverrideChange }) => {
  const { costReduction = 0.0, costPercentReduction, sellerProviderId, shippingServiceId } = cheapestOverride || emptyOverride;
  // TODO: Once we don't have to support both CheapestOverride and CheapestOverrideOptions, revisit the use of setCurrentOverride
  // and cheapestOverride/onShopperOverrideChange. We may be able to simplify this component by fully controlling it from the parent.

  // oxlint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentOverride, setCurrentOverride] = useState(cheapestOverride || emptyOverride);
  const [localCostReduction, setLocalCostReduction] = useState(costReduction);
  const [localCostPercentReduction, setLocalCostPercentReduction] = useState(costPercentReduction);
  const currencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const [selectedOption, setSelectedOption] = useState(costPercentReduction && costPercentReduction > 0 ? PERCENT_OPTION : CURRENCY_OPTION);
  const currencyInputDisabled = disabled || selectedOption !== CURRENCY_OPTION;
  const percentInputDisabled = disabled || selectedOption !== PERCENT_OPTION;

  const selectedServiceName = useMemo(() => {
    if (!shippingServiceId || !sellerProviderId) return null;
    const service = allowedCarrierServices.find(
      (item) => item.serviceId === shippingServiceId.toString() && item.sellerLabelProviderId === sellerProviderId.toString(),
    );
    return service ? `${service.name}${service.accountNickname ? ` (${service.accountNickname})` : ''}` : null;
  }, [allowedCarrierServices, sellerProviderId, shippingServiceId]);

  const onServiceSelection = useCallback(
    (item) => {
      setCurrentOverride((override) => {
        const newOverride = {
          ...override,
          sellerProviderId: item.sellerLabelProviderId,
          shippingServiceId: item.serviceId,
        };
        onShopperOverrideChange(newOverride);
        return newOverride;
      });
    },
    [onShopperOverrideChange],
  );

  const onCurrencyChange = useCallback(
    (value) => {
      setLocalCostReduction(value);
      setCurrentOverride((override) => {
        const newOverride = {
          ...override,
          costReduction: value,
          costPercentReduction: undefined,
        };
        onShopperOverrideChange(newOverride);
        return newOverride;
      });
    },
    [onShopperOverrideChange],
  );

  const onPercentChange = useCallback(
    (value) => {
      setLocalCostPercentReduction(value);
      setCurrentOverride((override) => {
        const newOverride = {
          ...override,
          costReduction: undefined,
          costPercentReduction: value,
        };
        onShopperOverrideChange(newOverride);
        return newOverride;
      });
    },
    [onShopperOverrideChange],
  );

  const onCheckboxChange = useCallback(
    (option) => {
      setSelectedOption(option);

      setCurrentOverride((override) => {
        const newOverride = {
          ...override,
          costReduction: option === CURRENCY_OPTION ? localCostReduction : undefined,
          costPercentReduction: option === PERCENT_OPTION ? localCostPercentReduction : undefined,
        };
        onShopperOverrideChange(newOverride);
        return newOverride;
      });
    },
    [localCostPercentReduction, localCostReduction, onShopperOverrideChange],
  );

  const focusOptionCheckbox = useCallback(
    (option) => {
      setSelectedOption(option);

      setCurrentOverride((override) => {
        const newOverride = {
          ...override,
          costReduction: option === CURRENCY_OPTION ? localCostReduction : undefined,
          costPercentReduction: option === PERCENT_OPTION ? localCostPercentReduction : undefined,
        };
        onShopperOverrideChange(newOverride);
        return newOverride;
      });
    },
    [localCostPercentReduction, localCostReduction, onShopperOverrideChange],
  );

  return (
    <div className={style['cheapest-override-options']}>
      <div className={style.service}>
        <div>
          <Text textType={TextType.Label} value={messages.useThisServiceText} />
        </div>

        <DropdownMenu disabled={disabled} variant='form'>
          <DropdownMenuToggler className={style['menu-toggler']}>
            {selectedServiceName || <FormattedMessage {...messages.selectServiceText} />}
          </DropdownMenuToggler>
          {allowedCarrierServices.map((service) => (
            <DropdownMenuItem key={service.serviceId} onClick={() => onServiceSelection(service)}>
              {service.name}
              {service.accountNickname ? ` (${service.accountNickname})` : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
      </div>

      <div className={style['cheapest-label']}>
        <Text textType={TextType.Label} value={messages.cheapestWithin} />
      </div>

      <div className={style['currency-option']}>
        <RadioCheckListItem
          id={CURRENCY_OPTION}
          isChecked={selectedOption === CURRENCY_OPTION}
          onChecked={() => onCheckboxChange(CURRENCY_OPTION)}
        >
          <FormattedMessage {...messages.cheapestOptionFixed} />
        </RadioCheckListItem>

        <CurrencyInput
          className={style['numeric-input']}
          currencyCode={currencyCode}
          disabled={currencyInputDisabled}
          onChange={onCurrencyChange}
          onFocus={() => focusOptionCheckbox(CURRENCY_OPTION)}
          showPlaceHolderForZero
          value={localCostReduction}
        />
      </div>

      <div className={style['percent-option']}>
        <RadioCheckListItem
          id={PERCENT_OPTION}
          isChecked={selectedOption === PERCENT_OPTION}
          onChecked={() => onCheckboxChange(PERCENT_OPTION)}
        >
          <FormattedMessage {...messages.cheapestOptionPercent} />
        </RadioCheckListItem>

        <PercentageInput
          className={style['numeric-input']}
          disabled={percentInputDisabled}
          onChange={onPercentChange}
          onFocus={() => focusOptionCheckbox(PERCENT_OPTION)}
          step={0.01}
          value={localCostPercentReduction}
        />
      </div>
    </div>
  );
};
